import React from 'react';
import { StyleSheet, Text as RNText, View } from 'react-native';

import { LogoImage } from './logo-image';

import { FoldTicks } from '@/components/print/fold-ticks';
import {
	PRINT_BOX_ROW_PAD, PRINT_FONT, PRINT_LINE, STATEMENT_PAGE_MARGIN, STATEMENT_SHEET_SIDE_PAD_IN, STATEMENT_SHEET_TOP_IN, TO_ADDRESS_INDENT, TO_ROW_GAP, headerMinHeight,
} from '@/components/print/print-type';
import type { AccountStatement } from '@/services/accounting-service';
import { formatBusinessDateUS } from '@/utils/date-utils';
import { statementCopy } from '@/utils/document-copy';
import { formatMoneyCents } from '@/utils/format-money';
import { formatOwnerName } from '@/utils/owner-name';
import { recipientAddress } from '@/utils/postal-address';

/**
 * Printable account statement sheet — the SBS reference layout shared by the
 * single-statement print page (`app/print/statement/[id].tsx`) and the bulk
 * run (`app/print/statements/index.tsx`). Per the repo rule, print and display
 * surfaces render ONE renderer; this is it. The wrapper pages own data
 * fetching, the toolbar, the `@page` print CSS, and auto-print — this
 * component is pure given an already-fetched `AccountStatement`.
 *
 * Each rendered sheet tags its root with `data-statement-sheet="1"`, so the
 * bulk wrapper's print CSS can force a page break between owners.
 */

// `fmtDate` is for business dates (occurredOn, statement window bounds — stored
// as UTC midnight). `fmtMoment` is for real moments-in-time (generatedAt, the
// print stamp's clock) which should reflect local time.
export const fmtDate = (ms: number): string => formatBusinessDateUS(ms);
export const fmtMoment = (ms: number): string => {
	// Moment-in-time (print clock) intentionally uses local time, not business-date UTC.
	/* eslint-disable no-restricted-syntax -- deliberate local-time moment, not a business date */
	const d = new Date(ms);
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${mm}/${dd}/${d.getFullYear()}`;
	/* eslint-enable no-restricted-syntax */
};
const fmtMoney = (c: number): string => formatMoneyCents(c).replace(/^\$/, '');

const transactionLabel = (e: AccountStatement['entries'][number]): string => {
	const ref = e.reference ? `INV ${e.reference}` : '';
	const cat = e.category ?? (e.kind === 'payment' ? 'Payment' : e.kind === 'credit' ? 'Credit' : e.kind === 'refund' ? 'Refund' : 'Charge');
	const memo = e.memo ? ` — ${e.memo}` : '';
	return [ref, cat].filter(Boolean).join(' ') + memo;
};

interface Props {
	data: AccountStatement;
	/** Contact-strip fallback when the association has no PM email on file. The
	 *  wrapper passes the signed-in user's email so replies still reach a human. */
	userEmail?: string;
}

export const StatementSheet: React.FC<Props> = ({ data, userEmail }) => {
	const { account, association, organization, holders, window: win, openingBalanceCents, closingBalanceCents, entries } = data;
	// Force the ledger to a fixed minimum row count so short statements still
	// fill the page — the Balance forward row counts as one, plus N entries,
	// padded with empty rows when the total is below the minimum.
	const MIN_TABLE_ROWS = 12;
	const padCount = Math.max(0, MIN_TABLE_ROWS - 1 - entries.length);
	const assoc = association;
	const org = organization;
	const orgCity = [org?.city, org?.state].filter(Boolean).join(', ');
	const orgCityZip = [orgCity, org?.zip].filter(Boolean).join(' ');
	const headerLines: string[] = [
		assoc?.name ?? '',
		org?.name ? `c/o ${org.name}` : '',
		org?.address ?? '',
		orgCityZip,
	].filter(Boolean);

	const ownerName = formatOwnerName(holders, account.externalName ?? '');
	// Company/legal name (LLC/trust) prints ABOVE the owner name, mirroring the
	// envelope (#227). First holder that carries one wins.
	const companyName = holders.find((h) => h.companyName?.trim())?.companyName?.trim();

	// Envelope-style address: bold name first line, then the c/o and the billing address,
	// or the physical address as a fallback — the same block the envelope prints.
	const { coLine, addressLines } = recipientAddress(account);

	// Property address compressed onto a single semicolon-joined line so the
	// "Regarding" box reads as one centered value beneath its header.
	const propertyAddressOneLine = (account.address ?? '')
		.split(/\r?\n/)
		.map((s) => s.trim())
		.filter(Boolean)
		.join('; ');
	const propertyLine = [propertyAddressOneLine].filter(Boolean).join(' ');

	// Remit-to line: prefer the org's dedicated remit_address (often a PO Box,
	// edited on the organization settings page), falling back to the composed
	// office address when admins haven't set one yet.
	const remitAddress = (org?.remitAddress?.trim()
		? org.remitAddress.split(/\r?\n/).map((s) => s.trim()).filter(Boolean).join('; ')
		: [
			org?.address,
			[org?.city, org?.state].filter(Boolean).join(', '),
			org?.zip,
		].filter(Boolean).join('; ').replace(/;\s+,/g, ',')
	);
	const payeeName = assoc?.name ?? '';

	// Callout, legal paragraph ($### / {{grace}} filled from the late-fee rule) and the
	// fixed FDCPA notice — shared with the invoice slip.
	const { calloutText, legalText, fdcpaText } = statementCopy(assoc, org?.name);

	// Contact-strip email goes to the PM who owns the association (so replies
	// reach the relationship owner, not whoever happened to run the print).
	// Falls back to the current user, then the org-level address.
	const printerEmail = assoc?.pmEmail ?? userEmail ?? org?.email ?? '';

	return (
		<View style={styles.sheet} {...({ dataSet: { printSheet: '1', statementSheet: '1' } } as any)}>
			<FoldTicks boxTopIn={STATEMENT_PAGE_MARGIN.top} sidePadIn={STATEMENT_SHEET_SIDE_PAD_IN} />
			{/* Top: org block (left) + Statement title + date box (right) */}
			<View style={styles.topRow} testID="print-header">
				<View style={styles.orgBlock}>
					{org?.logo ? <View style={styles.logoWrap}><LogoImage uri={org.logo} maxHeight={64} maxWidth={280} /></View> : null}
					{headerLines.map((line, i) => (
						<RNText
							key={i}
							style={i === 0 ? styles.orgFirstLine : styles.orgLine}
						>
							{line}
						</RNText>
					))}
				</View>
				<View style={styles.statementTitleBlock}>
					<RNText style={styles.statementTitle}>Statement</RNText>
					<View style={styles.stampStack}>
						<View style={styles.stampBox}>
							<RNText style={styles.stampLabel}>Statement Date</RNText>
							<RNText style={styles.stampValue}>{fmtMoment(data.generatedAt)}</RNText>
						</View>
						<View style={styles.stampBox}>
							<RNText style={styles.stampLabel}>Account #</RNText>
							<RNText style={styles.stampValue}>{account.accountNumber ?? account.id.slice(0, 8)}</RNText>
						</View>
					</View>
				</View>
			</View>

			{/* To (recipient) on left, Regarding + yellow callout stacked on right */}
			<View style={styles.toAndCalloutRow} testID="print-to-row">
				<View style={styles.toBlock}>
					<View style={styles.toLabelBox}>
						<RNText style={styles.toLabel}>To</RNText>
					</View>
					<View style={styles.toBox}>
						{companyName ? <RNText style={[styles.toLine, styles.toName]}>{companyName}</RNText> : null}
						<RNText style={[styles.toLine, styles.toName]}>{ownerName || '—'}</RNText>
						{coLine ? <RNText style={styles.toLine}>c/o {coLine}</RNText> : null}
						{addressLines.map((line, i) => (
							<RNText key={i} style={styles.toLine}>{line}</RNText>
						))}
					</View>
				</View>
				<View style={styles.calloutBlock}>
					<View style={styles.regardingLabelBox}>
						<RNText style={styles.regardingHeader}>Regarding</RNText>
					</View>
					<View style={styles.regardingValueBox}>
						<RNText style={styles.regardingValue}>{propertyLine || '—'}</RNText>
					</View>
					<View style={styles.calloutBox}>
						<RNText style={styles.calloutText}>{calloutText}</RNText>
						{org?.website ? (
							<RNText style={styles.calloutWeb}>{org.website}</RNText>
						) : null}
					</View>
				</View>
			</View>

			{payeeName || remitAddress ? (
				<View style={styles.remitBox} {...({ dataSet: { printKeep: '1' } } as any)}>
					<RNText style={styles.remitText}>
						Payment may be made PAYABLE TO {'"'}{payeeName}{'"'} and
					</RNText>
					{remitAddress ? (
						<RNText style={styles.remitText}>mail to {remitAddress}</RNText>
					) : null}
				</View>
			) : null}

			{/* Ledger table */}
			<View style={styles.table}>
				<View style={[styles.row, styles.headRow]}>
					<RNText style={[styles.cell, styles.colDate, styles.headCell]}>Date</RNText>
					<RNText style={[styles.cell, styles.colTxn, styles.headCell]}>Transaction</RNText>
					<RNText style={[styles.cell, styles.colAmount, styles.headCell, styles.right]}>Amount</RNText>
					<RNText style={[styles.cell, styles.colBalance, styles.headCell, styles.right]}>Balance</RNText>
				</View>

				{/* Opening / Balance forward row */}
				<View style={styles.row} {...({ dataSet: { statementRow: '1' } } as any)}>
					<RNText style={[styles.cell, styles.colDate]}>{fmtDate(win.startDate)}</RNText>
					<RNText style={[styles.cell, styles.colTxn, styles.italic]}>Balance forward</RNText>
					<RNText style={[styles.cell, styles.colAmount, styles.right]}> </RNText>
					<RNText style={[styles.cell, styles.colBalance, styles.right]}>{fmtMoney(openingBalanceCents)}</RNText>
				</View>

				{entries.map((e) => (
					<View
						key={`${e.kind}-${e.id}`}
						style={styles.row}
						{...({ dataSet: { statementRow: '1' } } as any)}
					>
						<RNText style={[styles.cell, styles.colDate]}>{fmtDate(e.occurredOn)}</RNText>
						<RNText style={[styles.cell, styles.colTxn]} numberOfLines={2}>{transactionLabel(e)}</RNText>
						<RNText style={[styles.cell, styles.colAmount, styles.right]}>{fmtMoney(e.signedAmountCents)}</RNText>
						<RNText style={[styles.cell, styles.colBalance, styles.right]}>{fmtMoney(e.runningBalanceCents)}</RNText>
					</View>
				))}

				{Array.from({ length: padCount }).map((_, i) => (
					<View key={`pad-${i}`} style={styles.row}>
						<RNText style={[styles.cell, styles.colDate]}> </RNText>
						<RNText style={[styles.cell, styles.colTxn]}> </RNText>
						<RNText style={[styles.cell, styles.colAmount, styles.right]}> </RNText>
						<RNText style={[styles.cell, styles.colBalance, styles.right]}> </RNText>
					</View>
				))}
			</View>

			{/* Account balance summary on the right */}
			<View style={styles.balanceRow}>
				<View style={{ flex: 1 }} />
				<View style={styles.balanceBox}>
					<RNText style={styles.balanceLabel}>Account Balance</RNText>
					<View style={styles.balanceValueBox}>
						<RNText style={styles.balanceValue}>{fmtMoney(closingBalanceCents)}</RNText>
					</View>
				</View>
			</View>

			{/* Legal paragraph (editable per-association) + FDCPA notice (hard-coded) */}
			<RNText style={styles.legal}>{legalText}</RNText>
			<RNText style={styles.legalNotice}>{fdcpaText}</RNText>

			{/* Contact strip — 2-row table: header / value, all bordered, centered */}
			<View style={styles.contactTable} {...({ dataSet: { printKeep: '1' } } as any)}>
				<View style={styles.contactRow}>
					<View style={styles.contactHeadCell}><RNText style={styles.contactLabel}>Phone #</RNText></View>
					<View style={styles.contactHeadCell}><RNText style={styles.contactLabel}>Fax #</RNText></View>
					<View style={[styles.contactHeadCell, { flex: 2 }]}><RNText style={styles.contactLabel}>E-Mail</RNText></View>
					<View style={[styles.contactHeadCell, styles.lastCell]}><RNText style={styles.contactLabel}>Web Site</RNText></View>
				</View>
				<View style={[styles.contactRow, styles.contactValueRow]}>
					<View style={styles.contactValueCell}><RNText style={styles.contactValue}>{org?.phone ?? ''}</RNText></View>
					<View style={styles.contactValueCell}><RNText style={styles.contactValue}>{org?.fax ?? ''}</RNText></View>
					<View style={[styles.contactValueCell, { flex: 2 }]}><RNText style={styles.contactValue}>{printerEmail}</RNText></View>
					<View style={[styles.contactValueCell, styles.lastCell]}><RNText style={styles.contactValue}>{org?.website ?? ''}</RNText></View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	sheet: {
		width: '100%', maxWidth: 950, backgroundColor: '#fff',
		paddingVertical: 28, paddingHorizontal: 32,
		borderRadius: 4, borderWidth: 1, borderColor: '#cfd8dc',
	},
	// Fixed minimum height so the To row lands on the envelope window (ENVELOPE.toTopIn) however
	// tall the org lines, title and stamps render.
	topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', minHeight: headerMinHeight(STATEMENT_SHEET_TOP_IN) },
	orgBlock: { flex: 1, paddingRight: 16 },
	logoWrap: { marginBottom: 8, alignSelf: 'flex-start' },
	orgFirstLine: { fontSize: PRINT_FONT.orgFirstLine, lineHeight: PRINT_LINE.orgFirstLine, fontWeight: '700', color: '#000' },
	orgLine: { fontSize: PRINT_FONT.orgLine, lineHeight: PRINT_LINE.orgLine, color: '#000' },
	statementTitleBlock: { alignItems: 'flex-end', minWidth: 240 },
	statementTitle: { fontSize: PRINT_FONT.title, lineHeight: PRINT_LINE.title, fontWeight: '700', color: '#000', marginBottom: 8 },
	stampStack: { width: 220 },
	stampBox: {
		flexDirection: 'row', alignItems: 'center',
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		marginTop: -1, // collapse shared border between stacked boxes
	},
	stampLabel: { fontSize: PRINT_FONT.stampLabel, lineHeight: PRINT_LINE.boxRow, fontWeight: '700', color: '#000', flex: 1 },
	stampValue: { fontSize: PRINT_FONT.stampValue, lineHeight: PRINT_LINE.boxRow, color: '#000', textAlign: 'right' },
	toAndCalloutRow: { flexDirection: 'row', marginTop: TO_ROW_GAP, gap: 16 },
	toBlock: { flex: 1 },
	toLabelBox: {
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	toLabel: { fontSize: PRINT_FONT.boxLabel, lineHeight: PRINT_LINE.boxRow, color: '#000', fontWeight: '700', textAlign: 'center' },
	toBox: {
		borderWidth: 1, borderColor: '#000', borderTopWidth: 0,
		// The window clips from the bottom, so the bottom padding stays at its pre-scale 10px: the
		// last address line ends exactly where it did before the type scale went up, and a 4-line
		// address still fills the same 73px box. The extra height comes out of the top padding.
		paddingTop: 6, paddingBottom: 10, paddingHorizontal: 10, minHeight: 70,
		alignItems: 'flex-start', justifyContent: 'center',
	},
	// Indented inside the box (whose size and border don't change) so the address sits nicer in the window.
	toLine: {
		fontSize: PRINT_FONT.address, lineHeight: PRINT_LINE.address, color: '#000', textAlign: 'left',
		paddingLeft: TO_ADDRESS_INDENT,
	},
	toName: { fontWeight: '700' },
	calloutBlock: { flex: 1.2 },
	regardingLabelBox: {
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	regardingHeader: { fontSize: PRINT_FONT.boxLabel, lineHeight: PRINT_LINE.boxRow, fontWeight: '700', color: '#000', textAlign: 'center' },
	regardingValueBox: {
		borderWidth: 1, borderColor: '#000', borderTopWidth: 0,
		paddingVertical: 6, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
	},
	regardingValue: { fontSize: PRINT_FONT.regarding, color: '#000', textAlign: 'center' },
	calloutBox: {
		backgroundColor: '#fff59d', borderWidth: 1, borderColor: '#000',
		padding: 8, minHeight: 50, marginTop: 6,
	},
	calloutText: { fontSize: PRINT_FONT.callout, color: '#000' },
	remitBox: {
		borderWidth: 1.5, borderColor: '#1b5e20',
		paddingVertical: 10, paddingHorizontal: 14,
		marginTop: 14,
		alignItems: 'center', justifyContent: 'center',
	},
	remitText: { fontSize: PRINT_FONT.remit, fontWeight: '700', color: '#1b5e20', textAlign: 'center' },
	calloutWeb: { fontSize: PRINT_FONT.callout, color: '#000', fontWeight: '600', marginTop: 4, textAlign: 'center' },
	table: { width: '100%', marginTop: 18, borderTopWidth: 1, borderColor: '#000' },
	row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 2.5, borderBottomWidth: 0.5, borderColor: '#cfd8dc' },
	headRow: { borderBottomWidth: 1.5, borderColor: '#000', paddingVertical: 4 },
	cell: { fontSize: PRINT_FONT.cell, color: '#000', paddingHorizontal: 4 },
	headCell: { fontWeight: '700', fontSize: PRINT_FONT.cell },
	colDate: { width: 80 },
	colTxn: { flex: 1, minWidth: 0 },
	colAmount: { width: 90 },
	colBalance: { width: 100 },
	right: { textAlign: 'right' },
	italic: { fontStyle: 'italic' },
	balanceRow: { flexDirection: 'row', marginTop: 16, alignItems: 'center' },
	balanceBox: { flexDirection: 'row', alignItems: 'center' },
	balanceLabel: { fontSize: PRINT_FONT.balanceLabel, fontWeight: '700', color: '#000', marginRight: 8 },
	balanceValueBox: {
		backgroundColor: '#fff59d', borderWidth: 1, borderColor: '#000',
		paddingVertical: 4, paddingHorizontal: 14, minWidth: 110, alignItems: 'flex-end',
	},
	balanceValue: { fontSize: PRINT_FONT.balanceValue, fontWeight: '700', color: '#000' },
	legal: { fontSize: PRINT_FONT.legal, color: '#000', marginTop: 18, lineHeight: PRINT_LINE.legal },
	legalNotice: { fontSize: PRINT_FONT.legal, color: '#000', marginTop: 8, lineHeight: PRINT_LINE.legal, fontStyle: 'italic' },
	contactTable: { borderWidth: 1, borderColor: '#000', marginTop: 8 },
	contactRow: { flexDirection: 'row' },
	contactValueRow: { borderTopWidth: 1, borderColor: '#000' },
	contactHeadCell: {
		flex: 1, paddingVertical: 4, paddingHorizontal: 6,
		borderRightWidth: 1, borderColor: '#000',
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	contactValueCell: {
		flex: 1, paddingVertical: 5, paddingHorizontal: 6,
		borderRightWidth: 1, borderColor: '#000',
		alignItems: 'center', justifyContent: 'center',
	},
	lastCell: { borderRightWidth: 0 },
	contactLabel: { fontSize: PRINT_FONT.contactLabel, fontWeight: '700', color: '#000', textAlign: 'center' },
	contactValue: { fontSize: PRINT_FONT.contactValue, color: '#000', textAlign: 'center' },
});

export default StatementSheet;
